/**
 * Alternative layout for sandbox detail page.
 * Terminal fills the top 65%; a tabbed bottom panel (35%) houses Network Audit,
 * Monitor, File Explorer, and optionally Connections.
 * Fills remaining viewport height with no page-level scroll.
 *
 * @module pages/layouts/AltLayout
 */

import { ReactNode, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { clsx } from 'clsx';
import {
  Globe,
  Activity,
  FolderOpen,
  Link2,
  ChevronDown,
  ChevronUp,
  ListTree,
  HardDrive,
} from 'lucide-react';

type IdeTab = 'audit' | 'monitor' | 'processes' | 'files' | 'connections' | 'volumes';

interface AltLayoutProps {
  /** Terminal panel content */
  terminal: ReactNode;
  /** Network audit panel content */
  networkAudit: ReactNode;
  /** Monitor panel content (stats + processes) */
  monitor: ReactNode;
  /** Processes panel content */
  processes?: ReactNode;
  /** File explorer panel content */
  fileExplorer: ReactNode;
  /** Connections panel content (optional, only shown in preview mode) */
  connections?: ReactNode;
  /** Volumes panel content (optional, shows mounted volumes) */
  volumes?: ReactNode;
  /** Called when the active bottom tab changes */
  onTabChange?: (tab: string) => void;
}

const baseTabs: { id: IdeTab; label: string; icon: typeof Globe }[] = [
  { id: 'audit', label: 'Network Audit', icon: Globe },
  { id: 'monitor', label: 'Monitor', icon: Activity },
  { id: 'processes', label: 'Processes', icon: ListTree },
  { id: 'files', label: 'Files', icon: FolderOpen },
];

const connectionsTab: { id: IdeTab; label: string; icon: typeof Globe } = {
  id: 'connections',
  label: 'Connections',
  icon: Link2,
};

const volumesTab: { id: IdeTab; label: string; icon: typeof Globe } = {
  id: 'volumes',
  label: 'Volumes',
  icon: HardDrive,
};

/**
 * Alternative layout: terminal on top (65%), tabbed bottom panel (35%), fills viewport.
 * Draggable horizontal divider to resize the split.
 */
export function AltLayout({
  terminal,
  networkAudit,
  monitor,
  processes,
  fileExplorer,
  connections,
  volumes,
  onTabChange,
}: AltLayoutProps) {
  const [activeTab, setActiveTab] = useState<IdeTab>('audit');
  const [collapsed, setCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  // bottomHeight is initialized once we know the available space (35% of it)
  const [bottomHeight, setBottomHeight] = useState<number | null>(null);

  const tabs = useMemo(() => {
    const result = [...baseTabs];
    if (connections) {
      result.push(connectionsTab);
    }
    if (volumes) {
      result.push(volumesTab);
    }
    return result;
  }, [connections, volumes]);

  // The initial top offset of the container in the document — this is stable
  // and does not change when the user scrolls (unlike getBoundingClientRect).
  const initialTopRef = useRef<number | null>(null);

  // Measure available height and set default 35% bottom panel.
  // We capture the element's offsetTop once on first measure, then only react
  // to viewport-size changes.  Using rect.top would create a feedback loop on
  // mobile: scrolling changes rect.top → recalculates height → container grows
  // → more scroll → rect.top goes even more negative → repeat.
  useEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;

      // Capture the stable document-relative top once
      if (initialTopRef.current === null) {
        const rect = el.getBoundingClientRect();
        initialTopRef.current = rect.top + window.scrollY;
      }

      // <main> padding: p-6 (24px) on mobile, lg:p-8 (32px) on desktop
      const bottomPad = window.innerWidth >= 1024 ? 32 : 24;
      // Use visualViewport height on mobile to account for browser chrome
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const h = Math.floor(vh - initialTopRef.current - bottomPad);
      setAvailableHeight(h);
      // Only set the initial bottom height once
      setBottomHeight((prev) => prev ?? Math.floor(h * 0.35));
    };

    // Delay initial measure to ensure layout has settled after render
    const raf = requestAnimationFrame(measure);

    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (collapsed || bottomHeight === null) return;
      e.preventDefault();
      setIsDragging(true);

      const startY = e.clientY;
      const startHeight = bottomHeight;
      const maxHeight = (availableHeight ?? 600) * 0.7;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const dy = startY - moveEvent.clientY;
        const newHeight = Math.max(120, Math.min(maxHeight, startHeight + dy));
        setBottomHeight(newHeight);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [bottomHeight, collapsed, availableHeight]
  );

  const handleTabClick = (tab: IdeTab) => {
    if (activeTab === tab && !collapsed) {
      // Clicking the active tab toggles collapse
      setCollapsed(true);
    } else {
      setActiveTab(tab);
      setCollapsed(false);
    }
    onTabChange?.(tab);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const tabContent: Record<IdeTab, ReactNode> = {
    audit: networkAudit,
    monitor: monitor,
    processes: processes ?? null,
    files: fileExplorer,
    connections: connections ?? null,
    volumes: volumes ?? null,
  };

  // On small viewports (mobile), enforce a minimum so both the terminal and
  // bottom panel remain usable.  When the minimum exceeds the available
  // viewport space the container will be taller than the viewport and the page
  // will scroll — that's intentional and preferable to squishing both panels.
  // 750px guarantees ~340px for the bottom panel (enough for a 2-col x 3-row
  // monitor grid) and ~400px for the terminal.
  const MIN_CONTAINER_HEIGHT = 750;
  const needsScroll = availableHeight !== null && availableHeight < MIN_CONTAINER_HEIGHT;
  const resolvedHeight = availableHeight ? Math.max(availableHeight, MIN_CONTAINER_HEIGHT) : 750;

  // When the minimum kicks in, give the bottom panel 45% so the monitor grid
  // has enough vertical room (tab bar 36px + ~300px content).
  const mobileBottomHeight = needsScroll ? Math.floor(MIN_CONTAINER_HEIGHT * 0.45) : null;

  return (
    <div ref={containerRef} className="flex flex-col" style={{ height: `${resolvedHeight}px` }}>
      {/* Terminal panel */}
      <div
        className={clsx(
          'overflow-hidden bg-white dark:bg-[#0a0a0a] border border-gray-100 dark:border-[#2a2a2a] rounded-t-xl border-b-0',
          // On desktop: flex-1 fills remaining space. On mobile-scroll: explicit
          // height so both panels get a fair share.
          needsScroll ? 'flex-shrink-0' : 'flex-1 min-h-[200px]'
        )}
        style={
          needsScroll
            ? { height: `${resolvedHeight - (mobileBottomHeight ?? 0) - 4}px` }
            : undefined
        }
      >
        {terminal}
      </div>

      {/* Resize handle */}
      {!collapsed && (
        <div
          className={`flex-shrink-0 h-1 cursor-row-resize flex items-center justify-center group transition-colors ${isDragging ? 'bg-primary-500/20' : 'hover:bg-gray-200 dark:hover:bg-[#2a2a2a]'}`}
          onMouseDown={handleMouseDown}
        >
          <div
            className={`h-0.5 w-8 rounded-full transition-colors ${isDragging ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-primary-400'}`}
          />
        </div>
      )}

      {/* Bottom tabbed panel */}
      <div
        className="flex-shrink-0 bg-white dark:bg-[#0a0a0a] border border-gray-100 dark:border-[#2a2a2a] rounded-t-xl rounded-b-xl overflow-hidden flex flex-col"
        style={{
          height: collapsed
            ? 'auto'
            : needsScroll
              ? `${mobileBottomHeight}px`
              : bottomHeight
                ? `${bottomHeight}px`
                : '35%',
        }}
      >
        {/* Tab bar */}
        <div className="flex items-center h-9 min-h-[36px] bg-gray-50 dark:bg-[#111] border-b border-gray-100 dark:border-[#2a2a2a] px-2 gap-0.5 flex-shrink-0 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleTabClick(id)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 h-full text-xs font-medium border-b-2 transition-colors whitespace-nowrap shrink-0',
                activeTab === id && !collapsed
                  ? 'border-primary-500 text-gray-900 dark:text-gray-100'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            type="button"
            onClick={toggleCollapsed}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            title={collapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {collapsed ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        </div>

        {/* Tab content */}
        {!collapsed && (
          <div className="flex-1 overflow-hidden min-h-0">{tabContent[activeTab]}</div>
        )}
      </div>
    </div>
  );
}
